import { useEffect, useState } from "react";
import type { FC } from "react";

import Layout from "./Layout";

import { getAnalysisDetailByType } from "utils/getAnalysisDetailByType";
import { getAssignmentDetailByType } from "utils/getAssignmentByType";

import { useAppDispatch } from "store/hooks";
import { setSimpleAnswer, setStructuredAnswer } from "store/slices/answer";

import type { UserAssignmentDetailFragment } from "api/generated";

import type { TTabs } from "./tabs.type";

interface Props {
  assignment: UserAssignmentDetailFragment | undefined | null;
  userAssignmentId: string;
  isUnavailable?: boolean;
}

export const AssignmentDetailPractice: FC<Props> = ({ assignment, userAssignmentId, isUnavailable = false }) => {
  const tabs: TTabs[] = ["Zadání úlohy", "Výsledky a rozbory"];

  // AIDEV-NOTE: Záložka patří k úloze, na které byla zvolena. Odvozená při renderu (ne reset v efektu), takže po změně
  // `userAssignmentId` se nevyrenderuje ani jednou záložka s řešením předchozí úlohy. [TD-487]
  const [selectedTab, setSelectedTab] = useState<{ forId: string; tab: TTabs }>({
    forId: userAssignmentId,
    tab: tabs[0],
  });
  const activeTab = selectedTab.forId === userAssignmentId ? selectedTab.tab : tabs[0];
  const dispatch = useAppDispatch();

  // AIDEV-NOTE: Slice `answer` je globální, předvybranou odpověď z něj čtou Detail/ABCD i ABCDOddelene. Úklid při změně
  // úlohy a při odchodu proto patří sem, do společné komponenty všech detailů, ne na jednotlivé stránky. Navigace
  // prev/next mění jen URL, stránka zůstane namontovaná a odpověď z předchozí úlohy by zůstala vybraná. [TD-486]
  useEffect(
    () => () => {
      dispatch(setSimpleAnswer([]));
      dispatch(setStructuredAnswer([]));
    },
    [userAssignmentId, dispatch],
  );

  // AIDEV-NOTE: Bez kopie do state: efekt by `data` předával o render později, takže `Layout` s novým `key` by se
  // namontoval s obsahem předchozí úlohy a komponenty seedující state z props by si ho nechaly. [TD-487]
  const data = assignment ?? null;

  const onSubmit = () => {
    setSelectedTab({ forId: userAssignmentId, tab: tabs[1] });
  };

  const changeActiveTab = (tab: TTabs | null) => {
    setSelectedTab({ forId: userAssignmentId, tab: tab ?? tabs[0] });
  };

  if (data === null) {
    /* TODO: translate */
    if (isUnavailable) return <h3 className={"my-5 text-center"}>Úloha je dočasně nedostupná</h3>;

    return <h3 className={"my-5 text-center"}>Nepodařilo se načíst detail úlohy</h3>;
  }

  return (
    <Layout
      key={userAssignmentId}
      assignment={getAssignmentDetailByType(data.assignment?.assignment?.assignmentType.type, data, onSubmit)}
      analysis={getAnalysisDetailByType(data.assignment?.assignment?.assignmentType.type, {
        assignment: data.assignment,
        id: userAssignmentId,
      })}
      tabs={tabs}
      activeTab={activeTab}
      changeActiveTab={changeActiveTab}
    />
  );
};
